import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import './FeedbackForm.css';

const FEEDBACK_CONFIG = {
    'human-only': {
        title: 'Feedback – Human-only Condition',
        badge: 'Human-only',
        likertQuestions: [
            {
                id: 'satisfaction',
                label: 'I am satisfied with the poem I created.',
            },
            {
                id: 'sense_of_control',
                label: 'I felt in control of the creative direction of the final poem.',
            },
            {
                id: 'enjoyment',
                label: 'The process of writing with the tool was enjoyable.',
            },
            {
                id: 'use_of_interface',
                label: 'The interface was easy and intuitive to use.',
            },
            {
                id: 'tagging',
                label: 'Tagging the final poem was a straightforward process for me.',
            },
            {
                id: 'tutorial',
                label: 'The tutorials helped me significantly in this process.',
            },
        ],
        openQuestions: [
            { id: 'curation_helpful', label: 'What was the best or most helpful part of using this interface for your poetry writing?' },
            { id: 'curation_modifications', label: 'If you could add, change, or remove one feature, what would it be?' },
            { id: 'curation_suggestions', label: 'Do you have any other comments, ideas, or suggestions for us?' },
        ],
    },
    'ai-only': {
        title: 'Feedback – AI-only Condition',
        badge: 'AI-only',
        likertQuestions: [
            {
                id: 'satisfaction',
                label: 'I am satisfied with the poem I created.',
            },
            {
                id: 'sense_of_control',
                label: 'I felt in control of the creative direction of the final poem.',
            },
            {
                id: 'enjoyment',
                label: 'The process of writing with the tool was enjoyable.',
            },
            {
                id: 'use_of_interface',
                label: 'The interface was easy and intuitive to use.',
            },
            {
                id: 'tagging',
                label: 'Tagging the final poem was a straightforward process for me.',
            },
            {
                id: 'tutorial',
                label: 'The tutorials helped me significantly in this process.',
            },
            {
                id: 'collaboration',
                label: 'The AI felt like a helpful and reliable creative partner.',
            },
        ],
        commandConfigQuestion: {
            id: 'command_config',
            label: 'Which command configuration did you use most often? (Select all that apply)',
            options: [
                'Creativity',
                'Poetic Element',
                'Rhyme Schema',
                'Source of Inspiration',
                'Not used them at all (the default setting)',
            ],
        },
        openQuestions: [
            { id: 'curation_helpful', label: 'What was the best or most helpful part of using this interface for your poetry writing?' },
            { id: 'curation_challenging', label: 'What was the most frustrating or confusing part of the experience?' },
            { id: 'curation_modifications', label: 'If you could add, change, or remove one feature, what would it be?' },
            { id: 'curation_suggestions', label: 'Do you have any other comments, ideas, or suggestions for us?' },
        ],
    },
    'co-write': {
        title: 'Feedback – Co-write Condition',
        badge: 'Co-write',
        likertQuestions: [
            {
                id: 'satisfaction',
                label: 'I am satisfied with the poem I created.',
            },
            {
                id: 'sense_of_control',
                label: 'I felt in control of the creative direction of the final poem.',
            },
            {
                id: 'enjoyment',
                label: 'The process of writing with the tool was enjoyable.',
            },
            {
                id: 'use_of_interface',
                label: 'The interface was easy and intuitive to use.',
            },
            {
                id: 'tagging',
                label: 'Tagging the final poem was a straightforward process for me.',
            },
            {
                id: 'tutorial',
                label: 'The tutorials helped me significantly in this process.',
            },
            {
                id: 'collaboration',
                label: 'The AI felt like a helpful and reliable creative partner.',
            },
        ],
        aiScaleLabel: 'Roughly what fraction of the final poem do you think came from the AI? (0 = none, 10 = all)',
        openQuestions: [
            { id: 'curation_helpful', label: 'What was the best or most helpful part of using this interface for your poetry writing?' },
            { id: 'curation_challenging', label: 'What was the most frustrating or confusing part of the experience?' },
            { id: 'curation_modifications', label: 'If you could add, change, or remove one feature, what would it be?' },
            { id: 'curation_suggestions', label: 'Do you have any other comments, ideas, or suggestions for us?' },
        ],
    },
    'curator': {
        title: 'Feedback – Curator Condition',
        badge: 'Curator',
        likertQuestions: [
            {
                id: 'satisfaction',
                label: 'I am satisfied with the poem I created.',
            },
            {
                id: 'sense_of_control',
                label: 'I felt in control of the creative direction of the final poem.',
            },
            {
                id: 'enjoyment',
                label: 'The process of writing with the tool was enjoyable.',
            },
            {
                id: 'use_of_interface',
                label: 'The interface was easy and intuitive to use.',
            },
            {
                id: 'tagging',
                label: 'Tagging the final poem was a straightforward process for me.',
            },
            {
                id: 'tutorial',
                label: 'The tutorials helped me significantly in this process.',
            },
            {
                id: 'collaboration',
                label: 'The AI felt like a helpful and reliable creative partner.',
            },
        ],
        commandConfigQuestion: {
            id: 'command_config',
            label: 'Which command configuration did you use most often? (Select all that apply)',
            options: [
                'Creativity',
                'Poetic Element',
                'Rhyme Schema',
                'Source of Inspiration',
                'Not used them at all (the default setting)',
            ],
        },
        aiScaleLabel: 'On a scale from 0 to 10, how much of your final poem was based on the AI\'s suggestions? (The higher number shows more AI usage)',
        openQuestions: [
            { id: 'curation_helpful', label: 'What was the best or most helpful part of using this interface for your poetry writing?' },
            { id: 'curation_challenging', label: 'What was the most frustrating or confusing part of the experience?' },
            { id: 'curation_modifications', label: 'If you could add, change, or remove one feature, what would it be?' },
            { id: 'curation_suggestions', label: 'Do you have any other comments, ideas, or suggestions for us?' },
        ],
    },
};

const LIKERT_VALUES = [1, 2, 3, 4, 5, 6, 7];

function ScenarioFeedbackPage() {
    const { scenario } = useParams();  // 'human-only', 'ai-only', 'co-write', 'curator'
    const navigate = useNavigate();
    const { search } = useLocation();
    const params = new URLSearchParams(search || '');
    const interfaceParam = params.get('interface'); // 'write' or 'annotate'
// MTurk params (present when launched from an ExternalQuestion HIT)
    const workerId =
        params.get('workerId') ||
        params.get('worker_id') ||
        '';

    const assignmentId =
        params.get('assignmentId') ||
        params.get('assignment_id') ||
        '';

    const hitId =
        params.get('hitId') ||
        params.get('hit_id') ||
        '';

    const turkSubmitTo =
        params.get('turkSubmitTo') ||
        params.get('turk_submit_to') ||
        '';
    const isMTurk = !!(workerId && assignmentId);
    const isPreview = assignmentId === 'ASSIGNMENT_ID_NOT_AVAILABLE';

    const config = FEEDBACK_CONFIG[scenario];

    const [likert, setLikert] = useState({});
    const [aiScore, setAiScore] = useState(0);
    const [open, setOpen] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const pid = localStorage.getItem('pid') || '';

    const isAnnotate = interfaceParam === 'annotate';
    const role = isAnnotate ? 'annotator' : 'creator';
    const interfaceMode = isAnnotate ? 'annotate' : 'write';

    useEffect(() => {
        if (!config) {
            setError('Invalid feedback scenario.');
        }
    }, [config]);

    if (!config) {
        return (
            <div className="registration-page">
                <div className="registration-card">
                    <p>Invalid feedback scenario.</p>
                </div>
            </div>
        );
    }

    const updateLikert = (id, value) => {
        setLikert(prev => ({ ...prev, [id]: value }));
    };

    const updateOpen = (id, value) => {
        setOpen(prev => ({ ...prev, [id]: value }));
    };

    // Toggle for multi-select questions (command_config)
    const toggleMultiSelect = (questionId, optionValue) => {
        setOpen(prev => {
            const currentVal = prev[questionId];
            const currentArr = Array.isArray(currentVal)
                ? currentVal
                : (typeof currentVal === 'string' && currentVal.length
                    ? currentVal.split('|')
                    : []);
            const exists = currentArr.includes(optionValue);
            const nextArr = exists
                ? currentArr.filter(v => v !== optionValue)
                : [...currentArr, optionValue];
            return { ...prev, [questionId]: nextArr };
        });
    };

    const submitToMTurk = ({ assignmentId, workerId, hitId, turkSubmitTo, pid, scenario, interfaceMode, role, aiScore, likert, openToSend }) => {
        if (!assignmentId || assignmentId === 'ASSIGNMENT_ID_NOT_AVAILABLE' || !turkSubmitTo) {
            return false;
        }

        const action = `${turkSubmitTo.replace(/\/$/, '')}/mturk/externalSubmit`;

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = action;

        const addHidden = (name, value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value == null ? '' : String(value);
            form.appendChild(input);
        };

        addHidden('assignmentId', assignmentId);

        addHidden('worker_id', workerId);
        addHidden('hit_id', hitId);
        addHidden('pid', pid);
        addHidden('scenario', scenario);
        addHidden('interface', interfaceMode);
        addHidden('role', role);
        addHidden('ai_contribution', aiScore);
        addHidden('likert_json', JSON.stringify(likert));
        addHidden('open_json', JSON.stringify(openToSend));
        addHidden('study_status', 'completed');

        document.body.appendChild(form);
        form.submit();
        return true;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!pid) {
            setError('Missing participant ID. Please make sure you accessed this page from the study flow.');
            return;
        }
        if (isMTurk && isPreview) {
            setError('Please accept the HIT in MTurk before submitting.');
            return;
        }
        // --- REQUIRED: all Likert questions must be answered ---
        for (const q of config.likertQuestions) {
            if (!likert[q.id]) {
                setError('Please answer all 1–7 rating questions before submitting.');
                return;
            }
        }

        // --- REQUIRED: command configuration when present ---
        if (config.commandConfigQuestion) {
            const qid = config.commandConfigQuestion.id;
            const raw = open[qid];
            const arr = Array.isArray(raw)
                ? raw
                : (typeof raw === 'string' && raw.length ? raw.split('|') : []);
            if (arr.length === 0) {
                setError('Please select at least one command configuration option.');
                return;
            }
        }

        // Prepare open answers: convert arrays (like command_config) to "a|b|c" strings
        const openToSend = { ...open };
        Object.keys(openToSend).forEach(key => {
            if (Array.isArray(openToSend[key])) {
                openToSend[key] = openToSend[key].join('|');
            }
        });

        setSubmitting(true);
        try {
            const res = await fetch(`/api/feedback/${encodeURIComponent(scenario)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pid,
                    role,
                    interface: interfaceMode,
                    likert,
                    ai_contribution: aiScore, // required scale (0–10) – default 5 is still a valid answer
                    open: openToSend,
                }),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok || !data || !data.success) {
                throw new Error((data && data.error) || 'Failed to submit feedback.');
            }

            setSubmitted(true);

            const sentToMTurk = submitToMTurk({
                assignmentId,
                workerId,
                hitId,
                turkSubmitTo,
                pid,
                scenario,
                interfaceMode,
                role,
                aiScore,
                likert,
                openToSend,
            });

            if (isMTurk && !isPreview && !sentToMTurk) {
                throw new Error('Feedback was saved, but MTurk submission parameters are missing.');
            }

        } catch (err) {
            console.error(err);
            setError(err.message || 'Server error while submitting feedback.');
        } finally {
            setSubmitting(false);
        }
    };

    const goHome = () => navigate('/');

    const commandConfig = config.commandConfigQuestion || null;

    return (
        <div className="registration-page">
            <div className="registration-card">
                <button className="reg-back-link" onClick={goHome}>
                    ← Back to Main Menu
                </button>

                <div className="registration-header">
                    <div>
                        <h1 className="registration-title">{config.title}</h1>
                        <p className="registration-subtitle">
                            Please answer the questions below about your experience in this condition. Your responses help us improve the study.
                        </p>
                    </div>
                    <div className="registration-badge">{config.badge}</div>
                </div>

                <form className="registration-form" onSubmit={onSubmit}>
                    {/* Likert section */}
                    {config.likertQuestions.map(q => (
                        <div key={q.id} className="reg-field">
                            <div className="reg-label-row">
                                <span className="reg-label">{q.label}</span>
                                <span className="reg-required">*</span>
                            </div>

                            <div className="feedback-scale-row">
                                {LIKERT_VALUES.map(v => (
                                    <label key={v} className="feedback-scale-option">
                                        <input
                                            type="radio"
                                            name={q.id}
                                            value={v}
                                            checked={String(likert[q.id] || '') === String(v)}
                                            onChange={() => updateLikert(q.id, v)}
                                        />
                                        <span>{v}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="feedback-likert-labels">
                                <span>1 = Strongly disagree</span>
                                <span>7 = Strongly agree</span>
                            </div>
                        </div>
                    ))}

                    {/* AI contribution 0–10 (always shown, with safe fallback label) */}
                    <div className="reg-field">
                        <div className="reg-label-row">
                            <span className="reg-label">
                                {config.aiScaleLabel ||
                                    'On a scale from 0 to 10, how much of this poem do you feel came from the AI?'}
                            </span>
                            <span className="reg-required">*</span>

                        </div>
                        <div className="feedback-ai-scale-wrapper">
                            <div className="feedback-ai-scale-row">
                                <span>0</span>
                                <input
                                    type="range"
                                    min="0"
                                    max="10"
                                    value={aiScore}
                                    onChange={(e) => setAiScore(Number(e.target.value))}
                                />
                                <span>10</span>
                                <span className="feedback-ai-scale-value">{aiScore}</span>
                            </div>
                        </div>
                    </div>

                    {/* Curator / AI-only command configuration multi-select */}
                    {commandConfig && (
                        <div className="reg-field">
                            <div className="reg-label-row">
                                <span className="reg-label">
                                    {commandConfig.label}
                                </span>
                                <span className="reg-required">*</span>
                            </div>
                            <div className="reg-checkbox-group">
                                {commandConfig.options.map(opt => {
                                    const currentVal = open[commandConfig.id];
                                    const currentArr = Array.isArray(currentVal)
                                        ? currentVal
                                        : (typeof currentVal === 'string' && currentVal.length
                                            ? currentVal.split('|')
                                            : []);
                                    const checked = currentArr.includes(opt);

                                    return (
                                        <label key={opt} className="reg-checkbox-option">
                                            <input
                                                type="checkbox"
                                                value={opt}
                                                checked={checked}
                                                onChange={() => toggleMultiSelect(commandConfig.id, opt)}
                                            />
                                            <span>{opt}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Open-ended questions */}
                    {config.openQuestions.map(q => (
                        <div key={q.id} className="reg-field">
                            <div className="reg-label-row">
                                <span className="reg-label">{q.label}</span>
                            </div>
                            <textarea
                                className="feedback-textarea"
                                value={open[q.id] || ''}
                                onChange={(e) => updateOpen(q.id, e.target.value)}
                            />
                        </div>
                    ))}

                    {error && <div className="reg-error">{error}</div>}
                    {submitted && (
                        <div className="reg-status">
                            Thank you! Your feedback has been submitted.
                        </div>
                    )}

                    <div className="reg-footer">
                        <button
                            type="submit"
                            className="reg-button-primary"
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting…' : 'Submit Feedback'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ScenarioFeedbackPage;
